<script setup lang="ts">
import { setCookieUniversal } from '~/utils/set-cookie-universal'

defineOptions({ name: 'LanguagesSwitching' })

interface Props {
	classRounded?: string
}

const props = withDefaults(defineProps<Props>(), {
	classRounded: 'rounded-md',
})

const switchLocalePath = useSwitchLocalePath()
const { locale, setLocaleCookie } = useI18n()
const { hasPreferenceConsent } = useCookiePreferenceConsent()

const _LOCALES = ['en', 'pt', 'fr'] as const
type LangLC = (typeof _LOCALES)[number]
type LangUC = Uppercase<LangLC>
type LangOption = { id: number; emoji: string; code: LangUC; label: string }
const MAP: Record<LangUC, LangLC> = { EN: 'en', PT: 'pt', FR: 'fr' }

const rawItems = [
	{ id: 1, emoji: '🇺🇸', code: 'EN' as const },
	{ id: 2, emoji: '🇵🇹', code: 'PT' as const },
	{ id: 3, emoji: '🇫🇷', code: 'FR' as const },
]
const items: LangOption[] = rawItems.map((o) => ({ ...o, label: `${o.emoji} ${o.code}` }))

const selected = ref<LangOption>(items[0])

const setPrefCookies = (lang: LangLC) => {
	setCookieUniversal('i18n_redirected', lang, { path: '/', maxAge: 60 * 60 * 24 * 365, sameSite: 'lax' })
}

const updateSelect = async (value: LangOption) => {
	const language = MAP[value.code]
	const path = switchLocalePath(language) || '/'

	if (hasPreferenceConsent()) {
		setLocaleCookie(language)
		setPrefCookies(language)
	}

	await navigateTo(path, { replace: true })
}

const setSelectedFromLocale = (current: LangLC) => {
	const found = items.find((i) => i.code === (current.toUpperCase() as LangUC))
	if (found) selected.value = found
}

setSelectedFromLocale(locale.value)
watch(locale, setSelectedFromLocale)
</script>

<template>
	<USelectMenu
		v-model="selected"
		:options="items"
		option-attribute="label"
		size="md"
		class="w-fit"
		variant="outline"
		:ui="{
			base: '!bg-transparent !ring-white !text-white',
			icon: {
				base: 'text-white',
			},
			rounded: props.classRounded,
		}"
		:ui-menu="{
			container: 'z-30',
		}"
		@change="updateSelect"
	>
		<!-- Label -->
		<template #label="{ selected: slotSelected }">
			<div class="flex items-center gap-2">
				<span class="text-lg leading-none">{{ slotSelected?.emoji }}</span>
				<span class="font-medium hidden sm:block">{{ slotSelected?.code }}</span>
			</div>
		</template>

		<!-- Options -->
		<template #option="{ option }">
			<div class="flex items-center gap-2">
				<span class="text-lg leading-none">{{ option.emoji }}</span>
				<span class="font-medium text-secondary-800">{{ option.code }}</span>
			</div>
		</template>
	</USelectMenu>
</template>
